/* eslint-disable react/no-unescaped-entities */
import React from 'react';
import { Link } from 'react-router-dom';
import PolicyLayout from '../../components/policies/PolicyLayout';

export const CookiePolicyPage = () => (
  <PolicyLayout
    title="Cookie Policy"
    effectiveDate="1 February 2026"
    lastUpdated="1 February 2026"
  >
    <p>
      This Cookie Policy explains how <strong>StudioScheduler</strong> (operated by Borrow A Geek
      Limited) uses cookies and similar technologies on our Platform. It should be read alongside
      our <Link to="/privacy">Privacy Policy</Link>.
    </p>

    <h2>1. What are cookies?</h2>
    <p>
      Cookies are small text files placed on your device (phone, tablet or computer) by websites
      you visit. Similar technologies (local storage, session storage, pixels) work in comparable
      ways. Cookies are widely used to make websites work efficiently and to provide information to
      the site's owner.
    </p>

    <h2>2. Categories of cookies we use</h2>

    <h3>Strictly necessary (always active)</h3>
    <p>
      These are essential for the Platform to function and cannot be disabled. They do not store
      personal data beyond what is needed for the service.
    </p>
    <ul>
      <li><code>ss_access</code> — your HttpOnly authentication token (session).</li>
      <li><code>ss_csrf</code> — CSRF double-submit protection token.</li>
      <li><code>ss_impersonator</code> — active only while a Super Admin is impersonating a Studio Owner.</li>
      <li><code>cookie_consent</code> — records your cookie consent choice (this policy would not exist without it).</li>
    </ul>

    <h3>Functional (with your consent)</h3>
    <p>
      These enable enhanced features and personalisation. They can be blocked without breaking core
      functionality.
    </p>
    <ul>
      <li>Recently viewed studios (stored in local storage).</li>
      <li>Preferred timezone / locale (stored in local storage).</li>
      <li>Basket / checkout state.</li>
    </ul>

    <h3>Analytics (with your consent)</h3>
    <p>
      When you opt in, we use privacy-respecting analytics to understand how the Platform is used
      and to improve it. If we introduce analytics tools, they will be listed here and no
      identifying data will be shared with third parties. Analytics is currently disabled by default.
    </p>

    <h3>Marketing / advertising</h3>
    <p>
      We do not currently use advertising or cross-site tracking cookies. Should we introduce them
      in future, they will be listed here and will only load with your explicit consent.
    </p>

    <h2>3. Third-party cookies from embedded providers</h2>
    <p>
      The following third parties may set cookies within embedded components (e.g., the Ryft
      Payments card form). Their cookies are governed by their own privacy policies:
    </p>
    <ul>
      <li><strong>Ryft Payments</strong> (payment SDK) — <a href="https://www.ryftpay.com/privacy" target="_blank" rel="noreferrer">ryftpay.com/privacy</a></li>
      <li><strong>Google Maps</strong> (studio location embed) — only loads on Studio detail pages.</li>
      <li><strong>Sentry</strong> (error monitoring) — loads a small tracing cookie for correlating errors across pages.</li>
    </ul>

    <h2>4. Managing your choices</h2>
    <p>
      When you first visit the Platform you are asked to Accept all, Reject non-essential, or
      Manage your choices. You can change your preferences at any time by clicking{' '}
      <strong>"Cookie preferences"</strong> at the bottom of any page (in the footer). Withdrawing
      consent will not affect the lawfulness of processing carried out before withdrawal.
    </p>
    <p>
      You can also manage or delete cookies through your browser settings. Blocking strictly
      necessary cookies may prevent you from logging in or completing a booking.
    </p>

    <h2>5. Do Not Track</h2>
    <p>
      We honour the Global Privacy Control (GPC) signal where technically feasible. Browsers
      broadcasting a GPC signal will have non-essential cookies rejected automatically.
    </p>

    <h2>6. Changes</h2>
    <p>
      We may update this Cookie Policy when we introduce, remove, or reconfigure cookies. The
      "Last updated" date at the top of the page reflects the most recent revision.
    </p>

    <h2>7. Contact</h2>
    <p>
      Questions about cookies:{' '}
      <a href="mailto:privacy@studioscheduler.ie">privacy@studioscheduler.ie</a>
    </p>
  </PolicyLayout>
);

export default CookiePolicyPage;
